import { useEffect, useState } from "react";
import { Plus, Pencil, Archive, RotateCcw, UserRound, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import api, { getFileUrl, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import ProfessionalModal from "@/components/team/ProfessionalModal";
import { PROFESSIONALS } from "@/constants/testIds";

/** Gestion de Profesionales (quien atiende turnos): distinto de Equipo (quien
 * tiene login). Un profesional puede quedar sin cuenta (personal que no usa
 * el sistema) o vincularse a una cuenta existente, incluido el Admin. */
export default function ProfessionalsPage() {
  const [professionals, setProfessionals] = useState([]);
  const [linkableUsers, setLinkableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const [profRes, usersRes] = await Promise.all([
        api.get("/professionals"),
        api.get("/professionals/linkable-users"),
      ]);
      setProfessionals(profRes.data);
      setLinkableUsers(usersRes.data);
    } catch (err) {
      toast({ title: "Error al cargar profesionales", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visible = professionals.filter((p) => (showArchived ? true : p.is_active));

  const handleSubmit = async (form, existing) => {
    const payload = {
      name: form.name.trim(),
      specialty: form.specialty.trim() || null,
      photo_url: form.photo_url || null,
      schedule: form.schedule,
      // Fix AZ (2026-10-30): pagina web
      show_on_landing: form.show_on_landing,
      is_featured_doctor: form.is_featured_doctor,
      bio: form.bio.trim() || null,
    };
    if (form.accessMode === "existing" && form.link_user_id) {
      payload.link_user_id = form.link_user_id;
    } else if (form.accessMode === "new") {
      payload.create_login = true;
      payload.login_email = form.login_email.trim();
      payload.login_password = form.login_password;
    }
    try {
      if (existing) {
        await api.put(`/professionals/${existing.id}`, payload);
        toast({ title: "Profesional actualizado", description: `${form.name} fue actualizado correctamente.` });
      } else {
        await api.post("/professionals", payload);
        toast({ title: "Profesional agregado", description: `${form.name} ahora puede atender turnos.` });
      }
      setModalOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleUnlink = async (professional) => {
    try {
      await api.put(`/professionals/${professional.id}`, { unlink_user: true });
      toast({ title: "Cuenta desvinculada", description: `${professional.name} ya no esta vinculado a una cuenta de login.` });
      setModalOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const toggleActive = async (professional) => {
    try {
      await api.put(`/professionals/${professional.id}`, { is_active: !professional.is_active });
      toast({
        title: professional.is_active ? "Profesional archivado" : "Profesional reactivado",
        description: `${professional.name} ${professional.is_active ? "ya no aparece" : "vuelve a aparecer"} en Agenda y Servicios.`,
      });
      load();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6" data-testid={PROFESSIONALS.page}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Profesionales</h1>
          <p className="text-base text-muted-foreground mt-1">Quien atiende turnos en tu clinica, con o sin acceso al sistema.</p>
        </div>
        <Button
          className="bg-brand hover:bg-brand-hover text-white"
          onClick={() => { setEditing(null); setModalOpen(true); }}
          data-testid={PROFESSIONALS.addButton}
        >
          <Plus className="w-4 h-4" /> Agregar profesional
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid={PROFESSIONALS.showArchivedToggle} />
        <span className="text-sm text-muted-foreground">Ver archivados</span>
      </div>

      <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-muted-foreground">Cargando profesionales...</p>
        ) : visible.length === 0 ? (
          <div className="p-12 text-center" data-testid={PROFESSIONALS.emptyState}>
            <UserRound className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Aun no agregaste profesionales.</p>
          </div>
        ) : (
          <table className="w-full" data-testid={PROFESSIONALS.table}>
            <thead>
              <tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground bg-[#F3F0EA]">
                <th className="text-left font-medium py-3 px-4">Nombre</th>
                <th className="text-left font-medium py-3 px-4 hidden md:table-cell">Especialidad</th>
                <th className="text-left font-medium py-3 px-4 hidden sm:table-cell">Acceso al sistema</th>
                <th className="text-right font-medium py-3 px-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} className={`border-b border-border hover:bg-background transition-colors ${!p.is_active ? "opacity-60" : ""}`} data-testid={`${PROFESSIONALS.rowPrefix}${p.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-9 h-9">
                        <AvatarImage src={getFileUrl(p.photo_url)} alt={p.name} />
                        <AvatarFallback className="bg-brand-light text-brand text-xs">{p.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-foreground font-medium">{p.name}</span>
                      {!p.is_active && <Badge variant="outline" className="text-[10px]">Archivado</Badge>}
                      {/* Fix AZ (2026-10-30): si aparece en la pagina web */}
                      {(p.is_featured_doctor || p.show_on_landing) && (
                        <Badge className="bg-[#F4E4D6] text-[#B5793A] border-none font-normal text-[10px]" data-testid={`${PROFESSIONALS.webBadgePrefix}${p.id}`}>
                          {p.is_featured_doctor ? "Doctora principal en la web" : "En la web"}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground hidden md:table-cell">{p.specialty || "-"}</td>
                  <td className="py-3 px-4 hidden sm:table-cell">
                    {p.linked_user ? (
                      <Badge className="bg-[#E3EDE1] text-[#5B7A54] border-none font-normal gap-1" data-testid={`${PROFESSIONALS.loginBadgePrefix}${p.id}`}>
                        <ShieldCheck className="w-3 h-3" /> {p.linked_user.name}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">Sin acceso al sistema</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(p); setModalOpen(true); }} data-testid={`${PROFESSIONALS.editButtonPrefix}${p.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleActive(p)}
                        data-testid={p.is_active ? `${PROFESSIONALS.archiveButtonPrefix}${p.id}` : `${PROFESSIONALS.reactivateButtonPrefix}${p.id}`}
                      >
                        {p.is_active ? <Archive className="w-4 h-4 text-destructive" /> : <RotateCcw className="w-4 h-4 text-brand" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ProfessionalModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        professional={editing}
        linkableUsers={linkableUsers}
        onSubmit={handleSubmit}
        onUnlink={handleUnlink}
      />
    </div>
  );
}
